import { cva, type VariantProps } from 'class-variance-authority';
import { transitionBase, uiMarker } from './shared';

/**
 * PERMISSION MATRIX
 *
 * The role editor in Settings lists permissions by category: a header row
 * with a select-all checkbox, the category name and a "3/5 permissions"
 * count, and under it one indented row per permission. It is the only
 * screen with this shape, so the parts are named here rather than generalised.
 *
 * Structure comes from Surface, Inline and Stack; these recipes carry only
 * what those do not draw — the header's hover, the body's rule, the row's
 * indent and its "differs from the role" tint.
 */

/** The clickable header row. `bg-card` so it reads as the lid of the box. */
export const permissionGroupHeaderRecipe = cva([
  uiMarker,
  'cursor-pointer bg-card p-4 hover:bg-accent/50',
  transitionBase,
]);

/** The rows beneath, on the page background, with a rule under the header. */
export const permissionGroupBodyRecipe = cva([
  uiMarker,
  'border-t border-border bg-background py-2',
]);

export const permissionRowRecipe = cva([uiMarker, 'py-2 pl-8'], {
  variants: {
    /**
     * This row's value differs from what the role would give the user. A
     * tint rather than a border, so a run of overrides reads as a band.
     */
    overridden: { true: 'bg-warning/5', false: '' },
  },
  defaultVariants: { overridden: false },
});

/**
 * The label beside the checkbox. Sized to the row rather than to Field's
 * label role, and laid out as a row so a badge can follow the words.
 */
export const permissionRowLabelRecipe = cva(
  [uiMarker, 'flex cursor-pointer select-none items-center gap-2 text-body'],
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed text-muted-foreground',
        false: '',
      },
    },
    defaultVariants: { disabled: false },
  }
);

export type PermissionRowRecipeProps = VariantProps<typeof permissionRowRecipe>;
export type PermissionRowLabelRecipeProps = VariantProps<
  typeof permissionRowLabelRecipe
>;
